import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiBox,
  MiCard,
  MiChip,
  MiModalConfirm,
  MiPlaceholderArea,
  MiSectionHeader,
  MiStackedTypography,
} from "@miplatform/components";
import { BUTTON_COLORS, ICONS_WEB, MODAL_TITLES } from "@miplatform/constants";
import { getCurrencyString } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import * as mdi from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import { Box, Divider, useTheme } from "@mui/material";
import {
  ACCOUNT_PRICING_DISPLAY_VALUES,
  ACCOUNT_PRICING_VALUES,
} from "../constants";
import { CategoryPricing } from "./CategoryPricing/CategoryPricing";
import { useAccountPricingTabViewModel } from "./useAccountPricingTabViewModel";

const generateSchema = (priceModifier = ACCOUNT_PRICING_VALUES.DOLLAR) => {
  const amountValidation =
    priceModifier === ACCOUNT_PRICING_VALUES.DOLLAR
      ? z
          .number({
            required_error: "Markup is required",
          })
          .gte(-99999.99, {
            message: "Markup ($) must be greater than or equal to -$99,999.99",
          })
          .lte(99999.99, {
            message: "Markup ($) must be less than or equal to $99,999.99",
          })
      : z
          .number({
            required_error: "Markup is required",
          })
          .gte(-1.0, {
            message: "Markup (%) must be greater than or equal to -1.0",
          })
          .lte(3.0, {
            message: "Markup (%) must be less than or equal to 3.0",
          });

  return z.object({
    startingPriceType: z.enum([
      ACCOUNT_PRICING_VALUES.LIST_PRICE,
      ACCOUNT_PRICING_VALUES.COST,
    ]),
    calculationType: z.enum([
      ACCOUNT_PRICING_VALUES.DOLLAR,
      ACCOUNT_PRICING_VALUES.PERCENT,
    ]),
    amount: amountValidation,
    draws: z.array(
      z.object({
        accountDrawId: z.string().uuid(),
        percentage: z.number({
          required_error: "Percentage is required",
        }),
      })
    ),
    drawTotal: z.number().refine((val) => val === 100, {
      message: "Total must equal 100%",
    }),
  });
};

export function AccountPricingTab({
  stateManager,
  accountDetail,
  setRequiresPriceConfig,
  accountDraws,
}) {
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [calculationType, setCalculationType] = useState(
    ACCOUNT_PRICING_VALUES.DOLLAR
  );

  const { classes } = useStyles();
  const theme = useTheme();

  const {
    categories,
    getCategories,
    selectedCategory,
    setSelectedCategory,
    updateCategory,
    isLoading,
  } = useAccountPricingTabViewModel({
    stateManager,
    setRequiresPriceConfig,
  });

  const {
    formState: { isValid, isDirty },
    control,
    reset,
    setValue,
    getValues,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      startingPriceType: ACCOUNT_PRICING_VALUES.LIST_PRICE,
      calculationType: ACCOUNT_PRICING_VALUES.DOLLAR,
      amount: 0,
      draws: [],
      drawTotal: 0,
    },
    resolver: zodResolver(generateSchema(calculationType)),
  });

  useEffect(() => {
    if (accountDetail?.id && accountDraws.length > 0) {
      getCategories(accountDetail.id);
    }
  }, [accountDetail?.id, accountDraws]);

  useEffect(() => {
    if (selectedCategory) {
      reset({
        startingPriceType: selectedCategory.startingPriceType,
        calculationType: selectedCategory.calculationType,
        amount: selectedCategory.amount,
        draws: selectedCategory.draws.sort(
          (a, b) => a.displayOrder - b.displayOrder
        ),
        drawTotal: selectedCategory.drawTotal,
      });
      // track calculation type for schema generation
      setCalculationType(selectedCategory.calculationType);
    }
  }, [selectedCategory]);

  const getCardBgColor = (isSelected, pendingConfiguration) => {
    if (isSelected) {
      return theme.palette.primary.light;
    }
    if (pendingConfiguration) {
      return theme.palette.orange.light;
    }
    return theme.palette.white.main;
  };

  const formatPricingVariable = (amount, calculationType) => {
    if (calculationType === ACCOUNT_PRICING_VALUES.PERCENT) {
      return `${amount.toFixed(2)}`;
    }
    return `${getCurrencyString(amount)}`;
  };

  const isCategoryNew = (category) => {
    return category.draws?.every((draw) => draw.percentage === 0);
  };

  return (
    <>
      <MiBox className={classes.root}>
        {accountDraws.length > 0 ? (
          <>
            <Box className={classes.leftContainer}>
              <MiSectionHeader title="Categories" />
              {categories.map((category) => {
                const isSelected =
                  selectedCategory?.categoryId === category.categoryId;
                const isPendingConfig = (category.drawTotal ?? 0) !== 100;
                const isNew = isCategoryNew(category);

                return (
                  <MiCard
                    key={category.categoryId}
                    title={category.categoryName}
                    titleColor={theme.palette.primary.main}
                    headerContainerStyles={{ margin: theme.spacing(1, 0) }}
                    style={{
                      backgroundColor: getCardBgColor(
                        isSelected,
                        isPendingConfig
                      ),
                    }}
                    className={classes.card}
                    onClick={() => {
                      if (!isSelected) {
                        setSelectedCategory(category);
                      }
                    }}
                    rightComponent={
                      <Box
                        className={classes.cardRightComponent}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box paddingRight={1}>
                          {isPendingConfig && (
                            <MiChip
                              label="Pending Configuration"
                              color="warning"
                              size="small"
                              sx={{
                                fontSize: 12,
                                borderRadius: 1,
                              }}
                            />
                          )}
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <MiStackedTypography
                          title="Pricing Basis"
                          descriptionProps={{
                            children: !isNew
                              ? ACCOUNT_PRICING_DISPLAY_VALUES[
                                  category?.startingPriceType
                                ]
                              : "--",
                          }}
                          style={{
                            margin: theme.spacing(0, 3),
                            width: 90,
                          }}
                        />
                        <Divider orientation="vertical" flexItem />
                        <MiStackedTypography
                          title="Pricing Modifier"
                          descriptionProps={{
                            children: (
                              <Box className={classes.pricingModifier}>
                                <span>
                                  {!isNew
                                    ? ACCOUNT_PRICING_DISPLAY_VALUES[
                                        category?.calculationType
                                      ]
                                    : "--"}
                                </span>
                                <span>
                                  {!isNew
                                    ? formatPricingVariable(
                                        category?.amount,
                                        category?.calculationType
                                      )
                                    : "--"}
                                </span>
                              </Box>
                            ),
                          }}
                          style={{
                            margin: theme.spacing(0, 3),
                            width: 120,
                          }}
                        />
                      </Box>
                    }
                  />
                );
              })}
            </Box>
            <Divider orientation="vertical" />
            <Box className={classes.rightContainer}>
              {selectedCategory ? (
                <Box className={classes.rightContent}>
                  <MiSectionHeader
                    title={selectedCategory?.categoryName}
                    rightIcon={mdi[ICONS_WEB.CLOSE]}
                    onRightIconClick={() => {
                      setSelectedCategory(null);
                    }}
                  />
                  <CategoryPricing
                    setCalculationType={setCalculationType}
                    selectedCategory={selectedCategory}
                    accountDetail={accountDetail}
                    getValues={getValues}
                    setValue={setValue}
                    control={control}
                  />
                </Box>
              ) : (
                <MiPlaceholderArea
                  showLogo
                  helperText="Select a Product Category to view additional information"
                />
              )}
            </Box>
          </>
        ) : (
          <MiPlaceholderArea
            showLogo
            helperText="Configure this customer's Accounting settings to access Pricing"
            containerStyle={{
              width: "100%",
            }}
          />
        )}
      </MiBox>
      <Footer
        show={selectedCategory}
        saveTitle="Save Category"
        isDirty={isDirty}
        handleSaveClick={() => {
          if (!isCategoryNew(selectedCategory)) {
            setOpenConfirmModal(true);
          } else {
            updateCategory(accountDetail.id, selectedCategory.categoryId, {
              ...getValues(),
              modifiedOn: selectedCategory.modifiedOn,
            });
          }
        }}
        handleCancelClick={() => setSelectedCategory(null)}
        disableSave={!isValid || !isDirty || isLoading}
      />
      <MiModalConfirm
        title={MODAL_TITLES.SAVE}
        disableClick={isLoading}
        textPositive="Yes, Update"
        open={openConfirmModal}
        isDirty={isDirty}
        handleNegative={() => setOpenConfirmModal(false)}
        handlePositive={() => {
          updateCategory(accountDetail.id, selectedCategory.categoryId, {
            ...getValues(),
            modifiedOn: selectedCategory.modifiedOn,
          });
          setOpenConfirmModal(false);
        }}
        description={`You are about to save changes to the ${accountDetail?.name} account. Are you sure you would like to continue?`}
        buttonPositiveColor={BUTTON_COLORS.PRIMARY}
      />
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    flex: 1,
    gap: theme.spacing(2),
  },
  leftContainer: {
    width: "50%",
    overflow: "auto",
    flex: 1,
  },
  rightContainer: {
    width: "50%",
  },
  rightContent: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  card: {
    border: `1px solid ${theme.palette.grey.accent}`,
    backgroundColor: "white",
    margin: theme.spacing(1),
    borderRadius: 4,
    padding: theme.spacing(0, 2),
    cursor: "pointer",
  },
  cardTitle: {
    display: "flex",
    gap: theme.spacing(2),
  },
  cardRightComponent: {
    display: "flex",
    alignItems: "center",
  },
  pricingModifier: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));
